import React from "react";
import {
  Box,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { StudyCardData, HealthStatus } from "./StudyCard";

const HEALTH_COLORS: Record<HealthStatus, string> = {
  "on-track": "#2E7D32",
  "monitor": "#ED6C02",
  "needs-attention": "#D32F2F",
  "no-data": "#9E9E9E",
};

interface StudyDetailData {
  meta: {
    study_id: number;
    study_code: string;
    study_name: string | null;
    researcher_email: string | null;
    status: string;
    study_type: string;
    study_created_at: string | null;
    study_setup_completed_at: string | null;
    first_participant_setup_at: string | null;
    status_changed_at: string | null;
    status_change_reason: string | null;
    last_activity_at: string | null;
    enrollment_target: number | null;
    enrollment_deadline: string | null;
  };
  counts: {
    invited: number;
    accepted: number;
    connected: number;
    currently_active: number;
    completed: number;
    withdrawn: number;
    expired: number;
    total: number;
    enrolled: number;
  };
  rates: {
    acceptance_rate: number | null;
    connection_rate: number | null;
  };
  status_history?: { old_status: string | null; new_status: string; changed_at: string; changed_by: string | null }[];
  participants?: { anonymized_id: string; status: string; invited_at?: string | null; accepted_at?: string | null; connected_at?: string | null }[];
}

interface StudyDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  study: StudyCardData | null;
  detail: StudyDetailData | null;
  loading: boolean;
  error: string | null;
}

const fmt = {
  date: (s?: string | null) => s ? new Date(s).toLocaleDateString() : "—",
  pct: (n: number | null) => n != null ? `${(n * 100).toFixed(1)}%` : "—",
};

function CountBar({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" fontWeight={500}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{value}</Typography>
      </Stack>
      <Box sx={{ height: 6, borderRadius: 3, bgcolor: "grey.100", overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${pct}%`, borderRadius: 3, bgcolor: color, transition: "width 0.4s ease" }} />
      </Box>
    </Box>
  );
}

const StudyDetailDrawer: React.FC<StudyDetailDrawerProps> = ({ open, onClose, study, detail, loading, error }) => {
  const healthColor = study ? HEALTH_COLORS[study.health_status] : "#9E9E9E";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 480 }, p: 0 } }}
    >
      {loading && <LinearProgress />}

      {/* Header */}
      <Box sx={{ p: 2.5, borderBottom: "1px solid", borderColor: "grey.200" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ flex: 1, mr: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              {study?.study_name || study?.study_code || "Study"}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{study?.study_code}</Typography>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: healthColor }} />
              <Typography variant="caption" sx={{ color: healthColor, fontWeight: 500 }}>
                {study?.health_reason}
              </Typography>
            </Stack>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2.5, overflow: "auto", flex: 1 }}>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        )}

        {detail && (
          <>
            {/* Metadata */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Study Info</Typography>
            <Grid container spacing={1} sx={{ mb: 2.5 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">PI</Typography>
                <Typography variant="body2">{detail.meta.researcher_email || "—"}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box><Chip size="small" label={detail.meta.status} /></Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2">{fmt.date(detail.meta.study_created_at)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Target</Typography>
                <Typography variant="body2">
                  {detail.meta.enrollment_target
                    ? `${detail.meta.enrollment_target} by ${fmt.date(detail.meta.enrollment_deadline)}`
                    : "Not set"}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2.5 }} />

            {/* Enrollment Funnel */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Enrollment Funnel</Typography>
            <CountBar label="Invited" value={detail.counts.invited} total={detail.counts.total || 1} color="#90CAF9" />
            <CountBar label="Accepted" value={detail.counts.accepted} total={detail.counts.total || 1} color="#42A5F5" />
            <CountBar label="Connected" value={detail.counts.connected} total={detail.counts.total || 1} color="#1E88E5" />
            <CountBar label="Active" value={detail.counts.currently_active} total={detail.counts.total || 1} color="#2E7D32" />
            <CountBar label="Completed" value={detail.counts.completed} total={detail.counts.total || 1} color="#7B1FA2" />
            <CountBar label="Withdrawn" value={detail.counts.withdrawn} total={detail.counts.total || 1} color="#F44336" />

            <Stack direction="row" spacing={3} sx={{ mt: 1, mb: 2.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Acceptance Rate</Typography>
                <Typography variant="body1" fontWeight={600}>{fmt.pct(detail.rates.acceptance_rate)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Connection Rate</Typography>
                <Typography variant="body1" fontWeight={600}>{fmt.pct(detail.rates.connection_rate)}</Typography>
              </Box>
            </Stack>

            <Divider sx={{ mb: 2.5 }} />

            {/* Participants */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Recent Participants ({detail.participants?.length || 0})
            </Typography>
            <Box sx={{ maxHeight: 200, overflow: "auto" }}>
              {detail.participants?.length ? (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                  <thead>
                    <tr>
                      {["ID", "Status", "Invited", "Accepted"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "4px 6px", borderBottom: "1px solid #eee", fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.participants.map((p, i) => (
                      <tr key={i}>
                        <td style={{ padding: "4px 6px", fontFamily: "monospace", fontSize: "0.65rem" }}>{p.anonymized_id.slice(0, 12)}...</td>
                        <td style={{ padding: "4px 6px" }}>{p.status}</td>
                        <td style={{ padding: "4px 6px" }}>{fmt.date(p.invited_at)}</td>
                        <td style={{ padding: "4px 6px" }}>{fmt.date(p.accepted_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <Typography variant="body2" color="text.secondary">No participants yet.</Typography>
              )}
            </Box>

            {/* Status History */}
            {detail.status_history && detail.status_history.length > 0 && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Status History</Typography>
                <Box sx={{ maxHeight: 150, overflow: "auto" }}>
                  {detail.status_history.slice(0, 20).map((h, i) => (
                    <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                        {fmt.date(h.changed_at)}
                      </Typography>
                      <Typography variant="caption">
                        {h.old_status || "—"} → {h.new_status}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default StudyDetailDrawer;
